import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Bell, CheckCheck } from 'lucide-react';
import { notificationsApi } from '@/api/platform';
import { cn } from '@/lib/cn';
import { formatDateTime, formatRelative } from '@/lib/dates';
import { qk } from '@/lib/queryKeys';
import { Popover } from '@/components/ui/Popover';
import { ErrorState } from '@/components/ui/Feedback';

/** Topbar bell: unread count polled every 30s; dropdown lists latest notifications; mark one/all read. */
export function NotificationsBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: qk.notifications,
    queryFn: () => notificationsApi.list(false),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
  const mark = useMutation({
    mutationFn: (ids?: number[]) => notificationsApi.markRead(ids),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.notifications }),
  });
  const unread = q.data?.unread ?? 0;

  return (
    <Popover
      label="Thông báo"
      className="w-[min(380px,calc(100vw-24px))] max-sm:fixed max-sm:inset-x-3 max-sm:top-[calc(3.5rem+0.375rem)] max-sm:mt-0 max-sm:w-auto"
      trigger={(p) => (
        <button
          type="button"
          {...p}
          aria-label={unread ? `Thông báo, ${unread} chưa đọc` : 'Thông báo'}
          className="relative inline-flex size-9 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-ink max-sm:size-11"
        >
          <Bell className="size-[18px]" aria-hidden />
          {unread > 0 && (
            <span className="absolute top-1 left-1/2 ml-0.5 min-w-4 rounded-full bg-danger px-1 text-center text-[10px] leading-4 font-semibold text-white ring-2 ring-surface tabular max-sm:top-2">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      )}
    >
      {(close) => (
        <div>
          <div className="flex items-center justify-between border-b border-line px-3 py-2.5">
            <p className="text-sm font-semibold">Thông báo</p>
            <button
              type="button"
              disabled={unread === 0 || mark.isPending}
              onClick={() => mark.mutate(undefined)}
              className="inline-flex items-center gap-1 text-xs text-accent hover:underline disabled:text-subtle disabled:no-underline"
            >
              <CheckCheck className="size-3.5" aria-hidden />
              Đánh dấu đã đọc tất cả
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {q.isError ? (
              <ErrorState error={q.error} onRetry={() => void q.refetch()} compact />
            ) : !q.data ? (
              <p className="px-3 py-6 text-center text-[13px] text-subtle">Đang tải…</p>
            ) : q.data.items.length === 0 ? (
              <p className="px-3 py-8 text-center text-[13px] text-subtle">Chưa có thông báo.</p>
            ) : (
              <ul className="divide-y divide-line">
                {q.data.items.slice(0, 30).map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      className={cn('flex w-full gap-2.5 px-3 py-2.5 text-left hover:bg-surface-2', !n.readAtUtc && 'bg-accent-soft/40')}
                      onClick={() => {
                        if (!n.readAtUtc) mark.mutate([n.id]);
                        if (n.link) {
                          close();
                          navigate(n.link.replace(/^https?:\/\/[^/]+/, ''));
                        }
                      }}
                    >
                      <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', n.readAtUtc ? 'bg-transparent' : 'bg-accent')} aria-hidden />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-medium text-ink">
                          {n.title}
                          {!n.readAtUtc && <span className="sr-only"> (chưa đọc)</span>}
                        </span>
                        {n.body && <span className="mt-0.5 line-clamp-2 block text-xs text-muted">{n.body}</span>}
                        <span className="mt-0.5 block text-2xs text-subtle" title={formatDateTime(n.createdAtUtc, { seconds: true })}>
                          {formatRelative(n.createdAtUtc)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Popover>
  );
}
